import { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { format, isSameMonth, addDays } from 'date-fns';
import OutsideClickHandler from 'react-outside-click-handler';
import { Box } from '@chakra-ui/react';

import { Condition } from 'Component/others';
import { CalendarIcon, ChevronDownIcon } from 'Asset/icon';
import { TextRegular } from 'UiKit/typography';
import useIsMobile from 'Hook/useIsMobile';

import styles from './styles.module.css';
import { isEmpty } from 'lodash';

const FilterDateRangePicker = ({
  disabled = false,
  endDate: propEndDate,
  error,
  label,
  maxDate,
  minDate,
  onChange = () => {},
  placeholder = 'Select date range',
  startDate: propStartDate,
  viewType = null,
  variant = 'default',
  icon = 'chevron',
  placement = 'center',
  ...props
}) => {
  const isMobile = useIsMobile();
  const [startDate, setStartDate] = useState(propStartDate);
  const [endDate, setEndDate] = useState(propEndDate);
  const [isOpen, setIsOpen] = useState(false);

  const iconSize = isMobile ? '18px' : '26px';

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);

    if (start && !end && viewType) {
      const autoEndDate = addDays(start, viewType.value - 1);
      setEndDate(autoEndDate);
      onChange({ startDate: start, endDate: autoEndDate });
      return;
    }

    if (start && end) onChange({ startDate: start, endDate: end });
  };

  const formatDateRange = () => {
    if (!startDate && !endDate) return placeholder;
    if (startDate && !endDate) return format(startDate, 'dd MMM yyyy');

    if (startDate && endDate) {
      if (isSameMonth(startDate, endDate)) return `${format(startDate, 'dd')} - ${format(endDate, 'dd')} ${format(endDate, 'MMM yyyy')}`;
      return `${format(startDate, 'dd MMM')} - ${format(endDate, 'dd MMM yyyy')}`;
    }

    return placeholder;
  };

  const getDateRangeClassName = () => {
    const baseClass = styles['daterange-custom'];
    const openClass = isOpen ? styles['daterange-custom--open'] : '';
    const variantClass = variant === 'outlined' ? styles['daterange-custom--outlined'] : '';
    return `${baseClass} ${openClass} ${variantClass}`;
  };

  const datepickerPopoverPlacement = () => {
    const dateRangeEl = document.getElementById('date-range__container');
    const popoverEl = document.getElementById('react-datepicker__container');

    switch (placement) {
      case 'left':
        popoverEl.style.left = 'unset';
        popoverEl.style.right = 0;
        break;

      case 'right':
        popoverEl.style.left = 0;
        popoverEl.style.right = 'unset';
        break;

      case 'center':
      default:
        if (isEmpty(dateRangeEl && popoverEl)) return;

        const popoverRect = popoverEl?.getBoundingClientRect();
        const left = dateRangeEl.offsetLeft + dateRangeEl.offsetWidth / 2 - popoverRect?.width / 2;

        popoverEl.style.left = `${left}px`;
        break;
    }
  };

  const handleDateRangeClick = () => {
    if (!disabled) setIsOpen(!isOpen);
  };

  const dateRange = formatDateRange();
  const dateRangeClassName = getDateRangeClassName();

  useEffect(() => {
    setStartDate(propStartDate);
    setEndDate(propEndDate);
  }, [propStartDate, propEndDate]);

  useEffect(() => {
    if (isOpen) datepickerPopoverPlacement();

    /* eslint-disable */
  }, [isOpen]);

  useEffect(() => {
    if (startDate && endDate) {
      setTimeout(() => setIsOpen(false), 250);
    }
  }, [startDate, endDate]);

  return (
    <Box className={`${styles['daterange-component__container']} ${disabled ? styles['daterange-component__container--disabled'] : ''}  `}>
      <Box id="date-range__container" className={dateRangeClassName} onClick={handleDateRangeClick}>
        <TextRegular>{dateRange} </TextRegular>

        <Condition statement={icon === 'calendar'}>
          <CalendarIcon size={iconSize} color="var(--color-semantic-text)" />
        </Condition>

        <Condition statement={icon === 'chevron'}>
          <ChevronDownIcon size={iconSize} color="var(--color-semantic-text)" />
        </Condition>
      </Box>
      <Condition statement={isOpen}>
        <OutsideClickHandler onOutsideClick={() => setIsOpen(false)}>
          <Box className={styles['react-datepicker__container']} id="react-datepicker__container">
            <DatePicker
              selected={startDate}
              startDate={startDate}
              endDate={endDate}
              maxDate={maxDate}
              minDate={minDate}
              formatWeekDay={(name) => name.charAt(0)}
              selectsRange
              inline
              onMonthChange={() => {}} // Required to prevent console warning
              onChange={handleDateChange}
              {...props}
            />
          </Box>
        </OutsideClickHandler>
      </Condition>
    </Box>
  );
};

export default FilterDateRangePicker;
